import store from "@/store";

export const PDF_CONG_TY_PLACEHOLDER =
  "...........................................";

/** Format accounting codes as `ma_tk/ma_ct` for PDF headers. */
export function formatTaiKhoanMaCt(
  maTk?: string | number | null,
  maCt?: string | number | null
): string {
  const tk = maTk != null ? String(maTk).trim() : "";
  const ct = maCt != null ? String(maCt).trim() : "";
  if (!tk || !ct) {
    return "";
  }
  return `${tk}/${ct}`;
}

/** Default document PDFs use Công ty + Đơn vị; master-data lists fall back to Công ty only. */
export type PdfUnitHeaderVariant = "document" | "masterList";

export interface PdfCongTyHeaderInput {
  tenDonVi?: string | null;
  tenDonViCha?: string | null;
  /**
   * Authenticated user's unit level. When `< 2` (company), `Công ty` uses
   * `tenDonVi` because `tenDonViCha` is null. Falls back to Vuex `capDonVi`
   * when omitted.
   */
  capDonVi?: number | null;
  variant?: PdfUnitHeaderVariant;
}

export interface PdfLabeledHeaderLine {
  label: string;
  value: string;
  /** When true, render only the value (bold) — no `Label:` prefix. */
  valueOnly?: boolean;
  /** When true, render the full `Label: value` line in bold. */
  bold?: boolean;
}

function displayOrDots(value: string): string {
  return value || PDF_CONG_TY_PLACEHOLDER;
}

function resolveAuthCapDonVi(explicit?: number | null): number | null {
  if (explicit != null && Number.isFinite(Number(explicit))) {
    return Number(explicit);
  }

  const fromStore = store.getters.capDonVi;
  return typeof fromStore === "number" ? fromStore : null;
}

/**
 * Company display name for the `Công ty` header row.
 * Company-level users (`cap_don_vi < 2`) have no parent — use `ten_don_vi`.
 */
export function resolveCongTyDisplayValue(
  tenDonVi: string,
  tenDonViCha: string,
  capDonVi?: number | null
): string {
  const cap = resolveAuthCapDonVi(capDonVi);
  if (cap != null && cap < 2) {
    return displayOrDots(tenDonVi);
  }
  return displayOrDots(tenDonViCha);
}

/**
 * Reads `ten_don_vi` / `ten_don_vi_cha` from an API response `extra` object.
 */
export function pickDonViPdfExtra(extra: unknown): {
  tenDonVi: string;
  tenDonViCha: string;
} {
  if (!extra || typeof extra !== "object") {
    return { tenDonVi: "", tenDonViCha: "" };
  }

  const record = extra as Record<string, unknown>;
  const tenDonVi =
    typeof record.ten_don_vi === "string" ? record.ten_don_vi.trim() : "";
  const tenDonViCha =
    typeof record.ten_don_vi_cha === "string"
      ? record.ten_don_vi_cha.trim()
      : "";

  return { tenDonVi, tenDonViCha };
}

/**
 * Resolves labeled unit header lines for PDF documents.
 *
 * - When both `tenDonVi` and `tenDonViCha` exist (any variant):
 *   bold `Công ty: ten_don_vi_cha` then `Đơn vị: ten_don_vi`
 * - document (default, missing a name): `Công ty: …` then `Đơn vị: …`
 * - masterList (missing a name): company name only (bold value, no label)
 *
 * `Công ty` value when a name is missing: `ten_don_vi` when auth
 * `cap_don_vi < 2`, else `ten_don_vi_cha`.
 */
export function resolveUnitHeaderLines(
  input: PdfCongTyHeaderInput
): PdfLabeledHeaderLine[] {
  const tenDonVi = (input.tenDonVi ?? "").trim();
  const tenDonViCha = (input.tenDonViCha ?? "").trim();
  const variant = input.variant ?? "document";
  const congTyValue = resolveCongTyDisplayValue(
    tenDonVi,
    tenDonViCha,
    input.capDonVi
  );

  // Both names present → unified Công ty / Đơn vị header (bold) for all PDF pages
  if (tenDonVi && tenDonViCha) {
    return [
      { label: "Công ty", value: tenDonViCha, bold: true },
      { label: "Đơn vị", value: tenDonVi, bold: true },
    ];
  }

  if (variant === "masterList") {
    return [
      {
        label: "Công ty",
        value: congTyValue,
        valueOnly: true,
      },
    ];
  }

  return [
    { label: "Công ty", value: congTyValue },
    { label: "Đơn vị", value: displayOrDots(tenDonVi) },
  ];
}

/** @deprecated Prefer resolveUnitHeaderLines */
export function resolveCongTyLines(input: PdfCongTyHeaderInput): string[] {
  return resolveUnitHeaderLines(input).map((line) => line.value);
}

export function formatUnitHeaderDisplayLines(
  input: PdfCongTyHeaderInput
): string[] {
  return resolveUnitHeaderLines(input).map((line) =>
    line.valueOnly ? line.value : `${line.label}: ${line.value}`
  );
}

/** @deprecated Prefer formatUnitHeaderDisplayLines */
export function formatCongTyDisplayLines(
  input: PdfCongTyHeaderInput
): string[] {
  return formatUnitHeaderDisplayLines(input);
}

export function formatCongTyLine(name: string): string {
  return `Công ty: ${name}`;
}
